import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Gift, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SEO } from '../../components/common/SEO';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';

const holidays = [
  { name: 'New Year\'s Day', date: 'January 1', category: 'major', description: 'Start the year with thoughtful gifts' },
  { name: 'Valentine\'s Day', date: 'February 14', category: 'major', description: 'Celebrate love with romantic gifts' },
  { name: 'Easter', date: 'March 31', category: 'religious', description: 'Spring celebration with family gifts' },
  { name: 'Mother\'s Day', date: 'May 12', category: 'major', description: 'Honor the special women in your life' },
  { name: 'Father\'s Day', date: 'June 16', category: 'major', description: 'Celebrate the dads and father figures' },
  { name: 'Independence Day', date: 'July 4', category: 'cultural', description: 'Patriotic celebration and summer gifts' },
  { name: 'Halloween', date: 'October 31', category: 'cultural', description: 'Spooky fun and costume accessories' },
  { name: 'Thanksgiving', date: 'November 28', category: 'major', description: 'Gratitude and family gathering gifts' },
  { name: 'Christmas', date: 'December 25', category: 'major', description: 'The biggest gift-giving holiday' },
  { name: 'Hanukkah', date: 'December 25-31', category: 'religious', description: 'Eight nights of celebration' },
  { name: 'Kwanzaa', date: 'December 26 - January 1', category: 'cultural', description: 'African heritage celebration' },
  { name: 'New Year\'s Eve', date: 'December 31', category: 'major', description: 'Ring in the new year with style' }
];

export const HolidayCalendarPage: React.FC = () => {
  const majorHolidays = holidays.filter(h => h.category === 'major');
  const religiousHolidays = holidays.filter(h => h.category === 'religious');
  const culturalHolidays = holidays.filter(h => h.category === 'cultural');
  const canonicalUrl = useCanonicalUrl();

  return (
    <>
      <SEO
        title="Holiday Calendar 2025 - Never Miss a Gift-Giving Occasion"
        description="Complete holiday calendar for 2025 with gift ideas, planning tips, and reminders. Never miss an important date again with our comprehensive holiday guide."
        keywords={['holiday calendar 2025', 'gift giving holidays', 'holiday planning', 'christmas', 'valentines day', 'mothers day', 'fathers day']}
        url={canonicalUrl}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              2025 Holiday Calendar
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Never miss an important gift-giving occasion again. Plan ahead with our comprehensive 
              holiday calendar featuring gift ideas, planning tips, and reminders.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button size="lg">
                  Start Planning
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/secret-santa-generator">
                <Button variant="outline" size="lg">
                  Organize Secret Santa
                </Button>
              </Link>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="text-center">
              <Calendar className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <h3 className="text-2xl font-bold text-gray-900">12</h3>
              <p className="text-gray-600">Major Holidays</p>
            </Card>
            <Card className="text-center">
              <Gift className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <h3 className="text-2xl font-bold text-gray-900">365</h3>
              <p className="text-gray-600">Days to Plan</p>
            </Card>
            <Card className="text-center">
              <Heart className="h-8 w-8 text-pink-500 mx-auto mb-2" />
              <h3 className="text-2xl font-bold text-gray-900">∞</h3>
              <p className="text-gray-600">Gift Ideas</p>
            </Card>
          </div>

          {/* Major Holidays */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Major Holidays</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {majorHolidays.map((holiday, index) => (
                <Card key={index} hover>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{holiday.name}</h3>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {holiday.date}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{holiday.description}</p>
                  <div className="flex space-x-2">
                    <Link to="/gift-guides">
                      <Button variant="outline" size="sm">
                        Gift Ideas
                      </Button>
                    </Link>
                    <Link to="/dashboard">
                      <Button size="sm">
                        Plan Gift
                      </Button>
                    </Link>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Religious Holidays */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Religious Holidays</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {religiousHolidays.map((holiday, index) => (
                <Card key={index} hover>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{holiday.name}</h3>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {holiday.date}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{holiday.description}</p>
                  <div className="flex space-x-2">
                    <Link to="/gift-guides">
                      <Button variant="outline" size="sm">
                        Gift Ideas
                      </Button>
                    </Link>
                    <Link to="/dashboard">
                      <Button size="sm">
                        Plan Gift
                      </Button>
                    </Link>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Cultural Holidays */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Cultural Holidays</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {culturalHolidays.map((holiday, index) => (
                <Card key={index} hover>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{holiday.name}</h3>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {holiday.date}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{holiday.description}</p>
                  <div className="flex space-x-2">
                    <Link to="/gift-guides">
                      <Button variant="outline" size="sm">
                        Gift Ideas
                      </Button>
                    </Link>
                    <Link to="/dashboard">
                      <Button size="sm">
                        Plan Gift
                      </Button>
                    </Link>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Planning Tips */}
          <section className="mb-12">
            <Card className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Holiday Planning Tips</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Start Early</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Create wishlists throughout the year</li>
                    <li>• Set reminders 2-3 months before major holidays</li>
                    <li>• Track prices to catch sales</li>
                    <li>• Plan budgets in advance</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Stay Organized</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Use our wishlist tools to track gifts</li>
                    <li>• Set up price alerts for expensive items</li>
                    <li>• Share wishlists with family and friends</li>
                    <li>• Keep track of what you've already given</li>
                  </ul>
                </div>
              </div>
            </Card>
          </section>

          {/* CTA */}
          <section className="text-center">
            <Card className="p-8 bg-white">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Plan Your Holidays?
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Create your first wishlist and never miss a gift-giving occasion again.
              </p>
              <Link to="/dashboard">
                <Button size="lg">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </Card>
          </section>
        </div>
      </div>
    </>
  );
};
